import { ChatSessionExpiredError } from 'assistant/domain/chatErrors'
import { type Thread, type ThreadPage } from 'assistant/domain/Thread'

import { type ChatTokenStore } from './chatTokenStore'

type ThreadRepositoryDependencies = {
  agentUrl: string
  tokenStore: Pick<ChatTokenStore, 'getToken' | 'invalidate'>
}

type ListResponse = { threads: Thread[]; page: number; hasMore: boolean }

export class ThreadRepository {
  private agentUrl: string
  private tokenStore: ThreadRepositoryDependencies['tokenStore']

  constructor({ agentUrl, tokenStore }: ThreadRepositoryDependencies) {
    this.agentUrl = agentUrl
    this.tokenStore = tokenStore
  }

  private async request(path: string, init: RequestInit = {}): Promise<Response> {
    const send = async () =>
      fetch(`${this.agentUrl}${path}`, {
        ...init,
        headers: {
          ...(init.body ? { 'Content-Type': 'application/json' } : {}),
          Authorization: `Bearer ${await this.tokenStore.getToken()}`,
        },
      })

    let response = await send()

    // A 401 means nothing ran, so one more try with a new chat token is safe.
    if (response.status === 401) {
      this.tokenStore.invalidate()
      response = await send()
    }

    if (response.status === 401) throw new ChatSessionExpiredError()

    return response
  }

  private async fail(response: Response): Promise<never> {
    const body = await response.text().catch(() => '')

    // Same message shape as the chat backend, so one function reads both.
    throw new Error(`Request failed with ${response.status}${body ? `: ${body}` : ''}`)
  }

  async list(page = 1): Promise<ThreadPage> {
    const response = await this.request(`/chat/threads?page=${page}`)

    if (!response.ok) return this.fail(response)

    const data = (await response.json()) as ListResponse

    return { threads: data.threads, page: data.page, hasMore: data.hasMore }
  }

  async rename(id: string, title: string): Promise<void> {
    const response = await this.request(`/chat/threads/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    })

    if (!response.ok) return this.fail(response)
  }

  async remove(id: string): Promise<void> {
    const response = await this.request(`/chat/threads/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    })

    // Already gone is what the user asked for.
    if (!response.ok && response.status !== 404) return this.fail(response)
  }
}
